import { api, type ResponseExportPage, type ResponseExportRow } from "../api";
import {
  assertResponseWorksheetSize,
  type ResponseDates, type ResponseExportQuiz,
} from "./responseExport";

export { RESPONSE_EXPORT_LIMITS } from "./responseExport";
const PAGE_SIZE = 200;
const INVALID_MESSAGE = "The response export is incomplete or invalid. Retry the download; if this continues, contact support.";

export function defaultResponseDates(now = new Date()): Required<ResponseDates> {
  const to = now.toISOString().slice(0, 10);
  const start = new Date(`${to}T00:00:00.000Z`);
  start.setUTCDate(start.getUTCDate() - 29);
  return { from: start.toISOString().slice(0, 10), to };
}

/** Unlike analytics' date helper, this endpoint has an exclusive upper bound. */
export function responseDateBounds(dates: ResponseDates): ResponseDates {
  if (dates.from === undefined && dates.to === undefined) return {};
  const valid = (date: string | undefined): date is string => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    const time = new Date(`${date}T00:00:00.000Z`);
    return Number.isFinite(time.getTime()) && time.toISOString().slice(0, 10) === date;
  };
  if (!valid(dates.from) || !valid(dates.to) || dates.from > dates.to) {
    throw new Error("Choose valid dates with the start date on or before the end date.");
  }
  const end = new Date(`${dates.to}T00:00:00.000Z`);
  end.setUTCDate(end.getUTCDate() + 1);
  return { from: `${dates.from}T00:00:00.000Z`, to: end.toISOString() };
}

function integer(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validatePage(value: unknown, slug: string, afterId: number): asserts value is ResponseExportPage {
  if (!record(value) || !Array.isArray(value.items) || value.items.length > PAGE_SIZE || !integer(value.total) || !integer(value.snapshot_id) || !(value.next_after_id === null || integer(value.next_after_id))) {
    throw new Error(INVALID_MESSAGE);
  }
  let previousId = afterId;
  for (const row of value.items) {
    const allowedSlug = record(row) && (row.quiz_slug === slug || (slug === "quiz-v6" && row.quiz_slug === "ignite"));
    if (!record(row) || !integer(row.id) || row.id <= previousId || row.id > value.snapshot_id || !allowedSlug || !(row.email === null || typeof row.email === "string") || typeof row.purchased !== "boolean" || !record(row.answers) || typeof row.created_at !== "string" || !/(Z|[+-]\d{2}:\d{2})$/.test(row.created_at) || !Number.isFinite(Date.parse(row.created_at))) {
      throw new Error(INVALID_MESSAGE);
    }
    previousId = row.id;
  }
  if (value.next_after_id !== null && (value.items.length === 0 || value.next_after_id !== previousId || previousId <= afterId)) {
    throw new Error(INVALID_MESSAGE);
  }
}

export function throwIfExportAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException("Download cancelled", "AbortError");
}

/** Validates and consumes one page at a time; the caller saves only after completion. */
export async function streamResponsePages(args: {
  quiz: ResponseExportQuiz;
  bounds: ResponseDates;
  signal: AbortSignal;
  onPage: (rows: ResponseExportRow[], total: number) => void | Promise<void>;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<number> {
  let loaded = 0;
  let afterId = 0;
  let snapshotId: number | undefined;
  let total: number | undefined;
  while (true) {
    throwIfExportAborted(args.signal);
    const page = await api.exportSubmissions({
      slug: args.quiz.slug, ...args.bounds, pageSize: PAGE_SIZE, afterId,
      ...(snapshotId === undefined ? {} : { snapshotId }),
    }, args.signal);
    throwIfExportAborted(args.signal);
    validatePage(page, args.quiz.slug, afterId);
    if ((snapshotId !== undefined && snapshotId !== page.snapshot_id) || (total !== undefined && total !== page.total)) {
      throw new Error("The response export changed during download. Retry the download.");
    }
    snapshotId = page.snapshot_id;
    total = page.total;
    assertResponseWorksheetSize(total, 5);
    const nextLoaded = loaded + page.items.length;
    if (nextLoaded > total || (page.next_after_id !== null && nextLoaded >= total) || (page.next_after_id === null && nextLoaded !== total)) {
      throw new Error(INVALID_MESSAGE);
    }
    await args.onPage(page.items, total);
    throwIfExportAborted(args.signal);
    loaded = nextLoaded;
    args.onProgress?.(loaded, total);
    if (page.next_after_id === null) return loaded;
    afterId = page.next_after_id;
  }
}
